import { FC, useCallback, useEffect, useMemo } from 'react';
import { useAtom } from 'jotai';
import { InlineNotification } from '@carbon/react';

import { DataTable, type DataTableColumnDef } from 'components/data-table';
import type { TableState } from 'components/data-table/types';
import { InterestSourcePill } from 'components/interest-source-pill';
import TooltipDecimal from 'components/tooltip-decimal/tooltip-decimal';
import { DEFAULT_PAGE_SIZE } from 'utils/page-sizes';

import { createReadOnlyDraftSlotsQueryAtoms } from '../atoms';
import { PropertyIdLink, UnitLink } from '../property-links';
import type { Step2SlotRow } from '../types';
import {
  draftSlotFilterStateToParams,
  mapDraftSlotsToStep2SlotRows,
  WELL_STATUS_OPTIONS,
} from '../utils';
import styles from './step2-slot-details-table.module.scss';

/** Props for {@link Step2SlotDetailsTable}. */
export interface Step2SlotDetailsTableProps {
  /** Active draft id. The fetch fires on mount/draftId-change. */
  draftId: string;
}

/**
 * This table's own read-only slots query (see `createReadOnlyDraftSlotsQueryAtoms`).
 */
const [, draftSlotDetailsQueryLoadableAtom] =
  createReadOnlyDraftSlotsQueryAtoms();

// Filter ids match the BE permits handled by `draftSlotFilterStateToParams`
// — only the BE-supported text/enum dimensions get a filter UI; the rest
// disable the column filter slot. Hole direction would be filterable on
// the BE but the inline filter UI for it isn't wired here yet.
const COLUMNS: DataTableColumnDef<Step2SlotRow>[] = [
  {
    id: 'slotId',
    accessorKey: 'slotId',
    header: 'Property ID',
    meta: { type: 'text' as const },
    // The displayed value is `uwi ?? slotId` per the mapper.
    cell: ({ row, getValue }) => (
      <PropertyIdLink
        propertyId={getValue<string>()}
        uwi={row.original.uwi}
        unitHref={row.original.unitHref}
      />
    ),
  },
  {
    id: 'unitName',
    accessorKey: 'unitName',
    header: 'Unit',
    meta: { type: 'text' as const },
    cell: ({ row, getValue }) => (
      <UnitLink
        unitName={getValue<string | null>()}
        unitHref={row.original.unitHref}
      />
    ),
  },
  {
    id: 'totalSlotNri',
    accessorKey: 'totalSlotNri',
    header: 'Total Property NRI',
    enableColumnFilter: false,
    enableSorting: false,
    cell: ({ getValue, row }) => (
      <span style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
        <TooltipDecimal value={getValue<number | null>()} precision={8} />
        <InterestSourcePill wiSource={row.original.wiSource} />
      </span>
    ),
  },
  {
    id: 'totalSlotWi',
    accessorKey: 'totalSlotWi',
    header: 'Total Property WI',
    enableColumnFilter: false,
    enableSorting: false,
    cell: ({ getValue, row }) => (
      <span style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
        <TooltipDecimal value={getValue<number | null>()} precision={8} />
        <InterestSourcePill wiSource={row.original.wiSource} />
      </span>
    ),
  },
  {
    id: 'wiVerified',
    accessorKey: 'wiVerified',
    header: 'WI Verified',
    enableColumnFilter: false,
  },
  {
    id: 'interestType',
    accessorKey: 'interestType',
    header: 'Ownership Type',
    enableColumnFilter: false,
  },
  {
    id: 'unitSource',
    accessorKey: 'unitSource',
    header: 'Unit Source',
    enableColumnFilter: false,
  },
  {
    id: 'unitDevelopmentType',
    accessorKey: 'unitDevelopmentType',
    header: 'Unit Development Type',
    enableColumnFilter: false,
  },
  {
    id: 'wellName',
    accessorKey: 'wellName',
    header: 'Well Name',
    enableColumnFilter: false,
  },
  {
    id: 'wellStatus',
    accessorKey: 'wellStatus',
    header: 'Well Status',
    meta: { type: 'enum' as const, filterOptions: WELL_STATUS_OPTIONS },
  },
  {
    id: 'typeCurveStatus',
    accessorKey: 'typeCurveStatus',
    header: 'Type Curve Status',
    enableColumnFilter: false,
  },
  // jscpd:ignore-start -- this table lays out its own columns at another row grain from SlotTable's; only this run of plain settings matches
  {
    id: 'operator',
    accessorKey: 'operator',
    header: 'Operator',
    meta: { type: 'text' as const },
  },
  {
    id: 'holeDirection',
    accessorKey: 'holeDirection',
    header: 'Hole Direction',
    enableColumnFilter: false,
  },
  {
    id: 'formation',
    accessorKey: 'formation',
    header: 'Formation',
    meta: { type: 'text' as const },
  },
  {
    id: 'slotNumber',
    accessorKey: 'slotNumber',
    header: 'Property #',
    meta: { type: 'integer' as const },
    enableColumnFilter: false,
  },
  // jscpd:ignore-end
  {
    id: 'permitDate',
    accessorKey: 'permitDate',
    header: 'Permit Date',
    meta: { type: 'date' as const },
    enableColumnFilter: false,
  },
  {
    id: 'spudDate',
    accessorKey: 'spudDate',
    header: 'Spud Date',
    meta: { type: 'date' as const },
    enableColumnFilter: false,
  },
  {
    id: 'rigReleaseDate',
    accessorKey: 'rigReleaseDate',
    header: 'Rig Release Date',
    meta: { type: 'date' as const },
    enableColumnFilter: false,
  },
  {
    id: 'completionDate',
    accessorKey: 'completionDate',
    header: 'Completion Date',
    meta: { type: 'date' as const },
    enableColumnFilter: false,
  },
  {
    id: 'firstProductionDate',
    accessorKey: 'firstProductionDate',
    header: 'First Production Date',
    meta: { type: 'date' as const },
    enableColumnFilter: false,
  },
];

/**
 * Step 2 read-only "Slot Details" table — slot-grain (one row per
 * draft slot, no tract fanout). Adapter-fed from `DraftSlot[]` via
 * {@link mapDraftSlotsToStep2SlotRows}, which mirrors the canonical
 * `mapSlotNRIResponseToSlotNRI` pattern used elsewhere in the app.
 *
 * NRI/WI cells render `<TooltipDecimal precision={8} />` for parity with
 * the Property Allocations table. Verification rollup icons + Calculation /
 * NRI Matrix popovers are deferred until BE emits a per-slot verification
 * rollup (today only per-tract subtract verifications are exposed on
 * `DraftTract.interestBreakdown`).
 *
 * @param {Step2SlotDetailsTableProps} props - Draft id
 * @return {JSX.Element} The slot-details table
 */
export const Step2SlotDetailsTable: FC<Step2SlotDetailsTableProps> = ({
  draftId,
}) => {
  const [queryState, fetchSlots] = useAtom(draftSlotDetailsQueryLoadableAtom);

  const loadData = useCallback(
    (params: TableState) => {
      fetchSlots({
        draftId,
        // `selected: true` is the read-only contract — baked in so callers
        // can't accidentally widen the scope.
        selected: true,
        page: params.page,
        pageSize: params.pageSize,
        text: params.globalFilter || undefined,
        ...draftSlotFilterStateToParams(params.columnFilters),
      });
    },
    [draftId, fetchSlots]
  );

  // DataTable doesn't self-fire `loadData` on mount in paginated mode;
  // kick off the initial fetch when the draft id changes.
  useEffect(() => {
    loadData({
      page: 1,
      pageSize: DEFAULT_PAGE_SIZE,
      globalFilter: '',
      sorting: [],
      columnFilters: [],
      selection: { selectAll: false, toggledNodes: [] },
      expanded: {},
    });
  }, [draftId, loadData]);

  const data = queryState.state === 'hasData' ? queryState.data : null;
  const rows = useMemo<Step2SlotRow[]>(
    () => (data ? mapDraftSlotsToStep2SlotRows(data.slots) : []),
    [data]
  );
  const totalCount = data?.meta.totalCount ?? 0;
  const isLoading = queryState.state === 'loading';

  const errorMessage: string | null =
    queryState.state === 'hasError'
      ? queryState.error instanceof Error
        ? queryState.error.message
        : 'An unexpected error occurred'
      : null;

  return (
    <div className={styles.container}>
      {errorMessage !== null && (
        <InlineNotification
          kind="error"
          title="Property details error"
          subtitle={errorMessage}
          hideCloseButton
          lowContrast
          className={styles.errorNotification}
        />
      )}
      <div className={styles.tableWrap}>
        <DataTable<Step2SlotRow>
          data={rows}
          columns={COLUMNS}
          getRowId={(row) => row.id}
          isLoading={isLoading}
          scroll={{ mode: 'paginated', serverDriven: true, totalCount }}
          onPaginationChange={loadData}
          onSortChange={loadData}
          onFilterChange={loadData}
          onGlobalSearchChange={loadData}
          config={{
            globalSearch: true,
            columnVisibility: true,
            columnFilters: true,
            // Match the BE-driven exporters' `draft-<id>-<table>.csv` shape so
            // a user's Downloads folder has consistent siblings:
            //   draft-<id>-assets.csv, draft-<id>-slots.csv,
            //   draft-<id>-slot_allocations.csv
            export: {
              exportPresets: [
                {
                  name: 'Export',
                  format: 'csv',
                  filename: `draft-${draftId}-slots`,
                },
              ],
            },
            sorting: false,
          }}
          searchPlaceholder="Search by Property ID or Well Name"
        />
      </div>
    </div>
  );
};
